import { type Address } from '@common/types/api/common';
import { type DateTimeUTC, type GUID, type InstallationId } from '@common/types/brandedTypes';

export enum RetailerType {
  Installer = 1,
  Partner = 2,
  ServiceProvider = 3,
  EnergyProvider = 4,
}

export enum RetailerLevel {
  Basic = 0,
  WpStarter = 1,
  Professional = 2,
  Premium = 3,
  ServicePoint = 4,
}

export interface RetailerLevelResponse {
  id: RetailerLevel;
  name: string | null;
}

export const retailerTypeNamesMap: Partial<Record<RetailerType, string>> = {
  [RetailerType.Installer]: 'common.retailer-type.installer',
  [RetailerType.EnergyProvider]: 'common.retailer-type.energy-provider',
};

export const retailerLevelNamesMap: Record<RetailerLevel, string> = {
  [RetailerLevel.Basic]: 'common.retailer-level.basic',
  [RetailerLevel.WpStarter]: 'common.retailer-level.wpstarter',
  [RetailerLevel.Professional]: 'common.retailer-level.professional',
  [RetailerLevel.Premium]: 'common.retailer-level.premium',
  [RetailerLevel.ServicePoint]: 'common.retailer-level.servicepoint',
};

interface RetailerApiAttribute {
  name: string;
  value: string;
}

interface RetailerApiClaims {
  attributes?: RetailerApiAttribute[];
}

export interface RetailerApiRequest {
  name: string;
  customerNumber?: string;
  email?: string;
  phoneNumber?: string;
  countryCode?: string;
  retailerType?: RetailerType;
  retailerLevel?: RetailerLevel;
  claims?: RetailerApiClaims;
}

export interface RetailerApiResponse extends RetailerApiRequest {
  id: GUID;
}

export type RetailerAttributesInternal = Record<string, string>;

export type RetailerResponseInternal = Omit<RetailerApiResponse, 'claims'> & {
  claims: {
    attributes: RetailerAttributesInternal;
  };
};

export interface Retailer {
  retailerId: string;
  retailerName?: string;
  customerNumber?: string;
  accessRight: RetailerAccessRight;
  canDelete: boolean;
}

export enum RetailerAccessRight {
  None = 0,
  Read = 10,
  Write = 20,
}

export interface RetailerAccessRequest {
  retailerId: string;
  installationId: InstallationId;
  accessRight: RetailerAccessRight;
  confirmationUrl?: string;
}

export interface RetailersInfoInstallationsQuery {
  offset: number;
  limit: number;
  sort?: string;
  retailerIds?: string[];
}

export interface RetailersInfoInstallationsResponse {
  retailerId: GUID;
  id: InstallationId;
  name?: string;
  createdUtc?: string;
  timeZoneId?: string;
  controlUnit?: string;
  location: Partial<Address>;
}

export interface RetailersInfoQuery {
  offset: number;
  limit: number;
  filter: string;
  sort: string;
}

export interface RetailersInfoResponse {
  id: GUID;
  name: string;
  email?: string;
  phoneNumber?: string;
  customerNumber?: string;
  retailerType?: RetailerType;
  statistics: {
    totalDeviceCount: number;
    currentYearDeviceIncreaseCount: number;
    totalUserCount: number;
    lastActivityUtc: string;
  };
  pendingDeletion?: {
    createdUtc: DateTimeUTC;
    scheduledUtc: DateTimeUTC;
    createdByUser: { email: string; };
  };
}

export type RetailerInfo = RetailerApiResponse & RetailersInfoResponse;

interface PendingRetailerAccessRetailer {
  id: GUID;
  name: string;
}

interface PendingRetailerAccessSender {
  id: GUID;
  firstName?: string;
  lastName?: string;
  email?: string;
}

interface PendingRetailerAccessInstallation {
  name: string;
  deviceType?: string;
}

export interface PendingRetailerAccessesResponse {
  id: GUID;
  retailer: PendingRetailerAccessRetailer;
  sender: PendingRetailerAccessSender;
  installation: PendingRetailerAccessInstallation;
  createdUtc: string;
  acceptedUtc?: string;
  rejectedUtc?: string;
  closedByOwner?: boolean;
}
